export const POLICY_UPDATED = "October 16, 2026";

export const policyData = [
  {
    slug: "payment-billing",
    title: "Payment & Billing Policy",
    summary: "How payments, authorizations, currencies, taxes, refunds, and billing issues are handled.",
    group: "Shopping",
    sections: [
      {
        title: "1. Available payment methods",
        paragraphs: [
          "Payment methods available for a particular order are displayed at checkout and may vary by location, device, order value, and payment provider. A payment option is not accepted unless it appears at checkout.",
          "By submitting payment details, you confirm that you are authorized to use the selected method and that the billing information is accurate.",
        ],
      },
      {
        title: "2. Authorization and capture",
        paragraphs: [
          "A temporary authorization may be placed when an order is submitted. The payment may be captured at order confirmation, during fulfillment, or when an item ships, depending on the provider and order type.",
          "An authorization is not always a completed charge. If an order is declined or cancelled, the bank or payment provider controls how quickly a pending authorization is released.",
        ],
      },
      {
        title: "3. Currency, taxes, and fees",
        paragraphs: [
          "The checkout page shows the transaction currency, merchandise total, shipping charge, and taxes collected by averlynxstore before the order is submitted. Your bank may apply conversion, cross-border, or other account fees that we do not control.",
          "Import duties, brokerage charges, and taxes not collected at checkout may be payable to local authorities or the carrier. See the Shipping & Delivery Policy for details.",
        ],
      },
      {
        title: "4. Failed, duplicate, or disputed payments",
        paragraphs: [
          "We may ask you to use another payment method if authorization fails or additional verification is needed. If you see a possible duplicate charge, contact us before opening a payment dispute so we can review the transaction promptly.",
          "Nothing in this section prevents you from using rights available through your card issuer or applicable law. Knowingly filing a false payment dispute may result in order cancellation or account restrictions.",
        ],
      },
      {
        title: "5. Refunds",
        paragraphs: [
          "Approved refunds are normally sent to the original payment method. Processing time after we issue a refund is controlled by the bank or payment provider. Store credit is used only when agreed, required by the original payment method, or permitted by applicable law.",
        ],
      },
    ],
  },
  {
    slug: "orders-cancellations",
    title: "Orders, Changes & Cancellations Policy",
    summary: "Order acceptance, stock issues, address changes, cancellations, and partial fulfillment.",
    group: "Shopping",
    sections: [
      {
        title: "1. Submitting an order",
        paragraphs: [
          "Submitting an order is an offer to buy the items in your cart. An automated acknowledgement confirms that we received the request; it does not by itself mean that the order has been accepted.",
          "An order is accepted when we send an acceptance or shipment confirmation, or when we otherwise begin fulfillment, subject to any mandatory rule that applies where you live.",
        ],
      },
      {
        title: "2. Review and availability",
        paragraphs: [
          "We may review an order for inventory, pricing, address, payment, security, and purchase-limit issues. If an item becomes unavailable, we may cancel that item, contact you for instructions, or ship the available items separately.",
          "You will not be charged for a cancelled item, and any captured amount for it will be refunded.",
        ],
      },
      {
        title: "3. Changes and address corrections",
        paragraphs: [
          "Contact cost@averlynxstore.one as soon as possible if you need to change a size, color, quantity, delivery address, or contact detail. We cannot guarantee a change after processing begins.",
          "For security, we may require cancellation and a new order instead of changing material payment or delivery information.",
        ],
      },
      {
        title: "4. Customer cancellations",
        paragraphs: [
          "You may request cancellation before fulfillment begins. If the order has already shipped, the Returns & Exchanges Policy applies. Any statutory cancellation or withdrawal right remains available regardless of this operational cutoff.",
        ],
      },
      {
        title: "5. Store cancellations",
        paragraphs: [
          "We may decline or cancel an order for a legitimate reason, including unavailable stock, an obvious pricing error, an undeliverable address, failed payment, suspected fraud, prohibited resale activity, or a legal restriction. We will provide notice and a refund for any amount collected for cancelled items.",
        ],
      },
    ],
  },
  {
    slug: "preorders-backorders",
    title: "Pre-Order & Backorder Policy",
    summary: "Rules that apply when products are offered before they are ready to ship.",
    group: "Shopping",
    sections: [
      {
        title: "1. When this policy applies",
        paragraphs: [
          "This policy applies only when a product page is clearly marked as pre-order, backorder, made-to-order, or otherwise not ready for immediate shipment. If no such label appears, the item is treated as a standard in-stock item.",
        ],
      },
      {
        title: "2. Estimated availability",
        paragraphs: [
          "Any release or shipment date is an estimate and may change because of production, quality-control, transport, or customs issues. We will communicate a material delay using the contact information supplied with the order.",
        ],
      },
      {
        title: "3. Payment and mixed orders",
        paragraphs: [
          "Checkout will indicate when payment is authorized or collected. Orders containing both available and pre-order items may ship together or separately. Any additional shipping charge must be shown before purchase or agreed with you.",
        ],
      },
      {
        title: "4. Cancellation and refund",
        paragraphs: [
          "Unless a product page clearly states a lawful, item-specific restriction, you may request cancellation before shipment. If we cannot supply the item, we will cancel it and refund the amount paid. Mandatory consumer cancellation rights are not limited.",
        ],
      },
    ],
  },
  {
    slug: "promotions-pricing",
    title: "Promotions, Discounts & Pricing Policy",
    summary: "Discount-code eligibility, exclusions, price corrections, adjustments, and returns.",
    group: "Shopping",
    sections: [
      {
        title: "1. Promotion terms",
        paragraphs: [
          "A promotion may have its own start and end time, territory, eligible products, minimum spend, customer limit, and redemption instructions. Those specific terms form part of this policy.",
          "Unless stated otherwise, offers cannot be combined, exchanged for cash, applied after an order is submitted, or used toward excluded items, taxes, shipping, or previously purchased goods.",
        ],
      },
      {
        title: "2. Discount codes",
        paragraphs: [
          "Codes must be entered correctly at checkout before the order is submitted. A code may be limited to one use per person, account, household, payment method, or order. Codes obtained through manipulation, unauthorized resale, or fraud may be rejected.",
        ],
      },
      {
        title: "3. Pricing and obvious errors",
        paragraphs: [
          "We aim to present accurate prices. If a price or discount is plainly incorrect, we may contact you for approval at the correct price or cancel the affected item and refund any collected amount. We will not rely on this clause to avoid a price we are legally required to honor.",
        ],
      },
      {
        title: "4. Price adjustments and matching",
        paragraphs: [
          "Unless a specific offer states otherwise, we do not guarantee competitor price matching or retroactive price adjustments. Any voluntary adjustment is limited to the terms communicated at the time.",
        ],
      },
      {
        title: "5. Returns from promotions",
        paragraphs: [
          "Refunds reflect the amount actually paid after discounts. If returning part of a bundle causes the remaining items to no longer qualify for an offer, the refund may be recalculated where the promotion terms and applicable law allow it.",
        ],
      },
    ],
  },
  {
    slug: "gift-cards-store-credit",
    title: "Gift Card & Store Credit Policy",
    summary: "Conditional terms for gift cards or store credit if these options are offered.",
    group: "Shopping",
    sections: [
      {
        title: "1. Availability",
        paragraphs: [
          "This policy applies only if averlynxstore makes gift cards or store credit available. The product page or credit notice will identify the value, currency, delivery method, permitted channel, and any additional terms before purchase or issue.",
        ],
      },
      {
        title: "2. Redemption",
        paragraphs: [
          "A valid code may be applied to eligible purchases up to its available balance. It cannot be used to buy another gift card unless expressly allowed. Any balance above the card or credit value must be paid using another accepted method.",
        ],
      },
      {
        title: "3. Expiration and fees",
        paragraphs: [
          "Expiration dates, inactivity fees, and cash-redemption limits vary by law. We will apply the rule shown when the value is issued, except where applicable law requires a longer period, prohibits a fee, or requires cash redemption.",
        ],
      },
      {
        title: "4. Loss, misuse, and fraud",
        paragraphs: [
          "Treat a gift-card code like cash. Contact us promptly about suspected theft or unauthorized use. Replacement is not guaranteed, but we may investigate when you provide proof of purchase and the unused code can be verified.",
        ],
      },
      {
        title: "5. Refunds",
        paragraphs: [
          "Gift-card purchases and promotional credits are generally not refundable except where required by law. A return paid with gift card or store credit will ordinarily be refunded to the same form of value, subject to applicable law.",
        ],
      },
    ],
  },
  {
    slug: "product-information-care",
    title: "Product Information & Care Policy",
    summary: "Product descriptions, materials, color, care instructions, and intended use.",
    group: "Products",
    sections: [
      {
        title: "1. Descriptions and images",
        paragraphs: [
          "We aim to describe each product accurately. Screen settings, lighting, photography, natural material variation, and production updates can cause small differences in color, texture, or detail. A difference is not considered minor when applicable law treats it as non-conformity or a misleading description.",
        ],
      },
      {
        title: "2. Materials and components",
        paragraphs: [
          "Material composition and notable features are listed on the product page or physical label when available. Components may change to improve availability or performance, but a material change will be reflected in the information supplied with the item.",
        ],
      },
      {
        title: "3. Care instructions",
        paragraphs: [
          "Follow the care label attached to the product. When online guidance differs from the physical label, the physical label controls. Damage caused by care that conflicts with those instructions may not qualify as a product defect, without affecting statutory rights.",
        ],
      },
      {
        title: "4. Intended use",
        paragraphs: [
          "Products should be used for their described purpose and with ordinary care. Clothing, footwear, and accessories are not professional protective or medical equipment unless explicitly identified and certified as such.",
        ],
      },
      {
        title: "5. Sensitivities",
        paragraphs: [
          "If you have a known allergy or sensitivity, review the listed materials and contact cost@averlynxstore.one before purchase. Stop using a product if irritation or an unexpected reaction occurs and seek appropriate professional advice when needed.",
        ],
      },
    ],
  },
  {
    slug: "sizing-fit",
    title: "Sizing & Fit Guidance",
    summary: "How to use measurements and fit descriptions when choosing apparel and footwear.",
    group: "Products",
    sections: [
      {
        title: "1. Guidance only",
        paragraphs: [
          "Size charts, model information, and fit descriptions are intended to help you choose; they do not guarantee an identical fit for every body shape or preference.",
        ],
      },
      {
        title: "2. Measuring",
        paragraphs: [
          "Use a flexible tape and compare your measurements with the chart shown for the specific product. Product-specific measurements take priority over a general chart. When between sizes, consider the intended silhouette, fabric stretch, and your preferred fit.",
        ],
      },
      {
        title: "3. Natural variation",
        paragraphs: [
          "Small measurement differences may occur because of material behavior and manufacturing tolerances. If the item materially differs from its description or is defective, the Returns & Exchanges Policy and your statutory rights apply.",
        ],
      },
      {
        title: "4. Help before ordering",
        paragraphs: [
          "For product-specific fit help, contact cost@averlynxstore.one with the product name and the measurements you want to compare. For privacy, do not send photographs or sensitive health information unless requested and necessary.",
        ],
      },
    ],
  },
  {
    slug: "warranty-defects",
    title: "Warranty & Defective Products Policy",
    summary: "Remedies for faulty, damaged, misdescribed, or non-conforming products.",
    group: "Products",
    sections: [
      {
        title: "1. Statutory rights",
        paragraphs: [
          "Products must meet the mandatory quality and conformity standards that apply to the sale. Nothing in this policy excludes a legal guarantee, implied warranty, or remedy that cannot lawfully be excluded.",
        ],
      },
      {
        title: "2. Reporting a problem",
        paragraphs: [
          "Contact cost@averlynxstore.one promptly with the order number, product name, a description of the issue, and clear photographs when useful. Keep the item and packaging until we provide instructions.",
        ],
      },
      {
        title: "3. Assessment",
        paragraphs: [
          "We may ask reasonable questions or inspect the product to distinguish a defect from ordinary wear, accidental damage, misuse, alteration, or care inconsistent with the label. An assessment does not reduce any burden or presumption imposed by applicable consumer law.",
        ],
      },
      {
        title: "4. Remedies",
        paragraphs: [
          "When a covered issue is confirmed, the available remedy may include repair, replacement, price reduction, or refund, depending on the circumstances and applicable law. We cover reasonable return costs when legally required or when we confirm that the item arrived faulty, damaged, or incorrect.",
        ],
      },
    ],
  },
  {
    slug: "product-safety-recalls",
    title: "Product Safety & Recall Policy",
    summary: "How safety reports, stop-use notices, withdrawals, and recalls are handled.",
    group: "Products",
    sections: [
      {
        title: "1. Safety reports",
        paragraphs: [
          "Report a suspected safety issue to cost@averlynxstore.one as soon as possible. Include the order number, product identifier, a factual description of what happened, and photographs if safe to take. For an urgent medical or emergency issue, contact local emergency services first.",
        ],
      },
      {
        title: "2. Stop-use instructions",
        paragraphs: [
          "If a product appears unsafe, stop using it and keep it away from children or others who may use it. Do not repair, discard, or return a potentially hazardous item until instructions are provided, unless immediate disposal is necessary for safety.",
        ],
      },
      {
        title: "3. Investigations and notices",
        paragraphs: [
          "We may work with suppliers, testing providers, carriers, marketplaces, or authorities to investigate. When a withdrawal, corrective action, or recall is required, we will provide the affected identifiers, risk, instructions, and available remedy through appropriate channels.",
        ],
      },
      {
        title: "4. Customer contact data",
        paragraphs: [
          "Order contact details may be used to deliver an important product-safety notice. These operational safety messages are not marketing and may be sent even if you opted out of promotional communications.",
        ],
      },
    ],
  },
  {
    slug: "accessibility",
    title: "Accessibility Statement",
    summary: "Our approach to inclusive access and how to request assistance or report a barrier.",
    group: "Legal & Privacy",
    sections: [
      {
        title: "1. Our commitment",
        paragraphs: [
          "averlynxstore wants customers with different abilities, technologies, and ways of interacting to be able to browse, understand, and use our website and services.",
        ],
      },
      {
        title: "2. Ongoing work",
        paragraphs: [
          "Accessibility is an ongoing process. We work to improve keyboard access, text alternatives, focus visibility, readable structure, contrast, form instructions, responsive layouts, and reduced-motion support where relevant. This statement does not claim that every page or third-party service fully conforms to a particular standard.",
        ],
      },
      {
        title: "3. Third-party content",
        paragraphs: [
          "Some payment, delivery, map, video, or other embedded services may be controlled by third parties. We encourage those providers to offer accessible experiences and will try to provide an alternative route when reasonably possible.",
        ],
      },
      {
        title: "4. Assistance and feedback",
        paragraphs: [
          "If you encounter a barrier or need product or policy information in another reasonable format, email cost@averlynxstore.one. Please identify the page, feature, assistive technology if relevant, and the type of help requested. We will respond as soon as reasonably possible.",
        ],
      },
    ],
  },
  {
    slug: "intellectual-property",
    title: "Intellectual Property & Rights Policy",
    summary: "Ownership, permitted use, third-party rights, and how to report possible infringement.",
    group: "Legal & Privacy",
    sections: [
      {
        title: "1. Respect for rights",
        paragraphs: [
          "averlynxstore respects copyright, trademark, design, publicity, and other intellectual-property rights. Content we publish should be original, licensed, authorized, or otherwise lawfully used. A reference to a third-party name or mark does not imply sponsorship or affiliation.",
        ],
      },
      {
        title: "2. Website content",
        paragraphs: [
          "Unless a notice says otherwise, the website's original text, layout, graphics, product photography, logos, and code are owned by averlynxstore or used with permission. You may view and use the site for personal shopping. No broader license is granted.",
        ],
      },
      {
        title: "3. Prohibited reuse",
        paragraphs: [
          "You may not copy, scrape, republish, sell, remove rights notices from, create confusingly similar branding from, or commercially exploit protected site content without the relevant rights holder's permission, except as allowed by law.",
        ],
      },
      {
        title: "4. Reporting alleged infringement",
        paragraphs: [
          "A rights holder or authorized representative may email cost@averlynxstore.one with: contact details; identification of the protected work or mark; the exact page or item at issue; the basis of the claim; a good-faith statement; and evidence of authority to act. Do not send unnecessary identity documents.",
        ],
      },
      {
        title: "5. Review and response",
        paragraphs: [
          "We may request clarification, restrict access to disputed material during review, notify the person who supplied it, or take other proportionate action. False or abusive notices may create liability. Nothing in this process determines ownership or replaces rights available under applicable law.",
        ],
      },
    ],
  },
  {
    slug: "acceptable-use",
    title: "Acceptable Use Policy",
    summary: "Rules that protect customers, rights holders, and the security of the website.",
    group: "Legal & Privacy",
    sections: [
      {
        title: "1. Lawful use",
        paragraphs: [
          "Use the website only for lawful purposes and in a way that does not infringe another person's rights, interfere with service, or mislead other users.",
        ],
      },
      {
        title: "2. Prohibited activity",
        bullets: [
          "Accessing accounts, systems, or data without authorization",
          "Introducing malware, harmful code, denial-of-service traffic, or abusive requests",
          "Circumventing purchase limits, security controls, or technical restrictions",
          "Using bots or scraping tools in a way that burdens the service or copies protected content",
          "Submitting fraudulent orders, false reviews, unlawful content, or another person's personal data without authority",
          "Impersonating averlynxstore or creating confusion about affiliation or endorsement",
        ],
      },
      {
        title: "3. Security research",
        paragraphs: [
          "Do not test or exploit a suspected vulnerability without written authorization. Report security concerns privately to cost@averlynxstore.one and allow a reasonable period for review before any public disclosure.",
        ],
      },
      {
        title: "4. Enforcement",
        paragraphs: [
          "We may rate-limit, block, suspend, cancel, preserve evidence, or report activity when reasonably necessary to protect customers, the service, or legal rights. Any action will be proportionate to the issue and subject to applicable law.",
        ],
      },
    ],
  },
  {
    slug: "reviews-user-content",
    title: "Reviews & User Content Policy",
    summary: "Standards for reviews, photos, feedback, moderation, and content permissions.",
    group: "Legal & Privacy",
    sections: [
      {
        title: "1. Honest contributions",
        paragraphs: [
          "If review, comment, photo, or other submission features are available, contribute only content based on your genuine experience. Disclose a free product, discount, employment, or other material connection when it could affect how readers understand the submission.",
        ],
      },
      {
        title: "2. Content standards",
        bullets: [
          "Do not submit content that is unlawful, threatening, discriminatory, deceptive, obscene, or unrelated to the product or service",
          "Do not include another person's private information or image without permission",
          "Do not copy text, images, music, logos, or other material unless you have the right to use it",
          "Do not submit malware, advertising, referral links, or repeated spam",
        ],
      },
      {
        title: "3. Permission to use submissions",
        paragraphs: [
          "You retain ownership of your content. You grant averlynxstore a non-exclusive, worldwide, royalty-free license to host, reproduce, format, display, and distribute the submission only for operating, promoting, and improving our products and services, subject to applicable law and any separate campaign terms. This license lasts while the content is used for those purposes and for reasonable backup and legal-retention periods.",
        ],
      },
      {
        title: "4. Moderation",
        paragraphs: [
          "We may reject or remove content that violates this policy, presents a legal or safety risk, contains personal data, or is not relevant. We do not promise to publish every submission or to verify every factual claim.",
        ],
      },
      {
        title: "5. Rights and privacy requests",
        paragraphs: [
          "To report infringement, request removal of personal information, or ask about a moderation decision, email cost@averlynxstore.one with the page and enough information to locate the content.",
        ],
      },
    ],
  },
  {
    slug: "marketing-communications",
    title: "Marketing Communications Policy",
    summary: "Consent, subscription choices, unsubscribe methods, and service messages.",
    group: "Legal & Privacy",
    sections: [
      {
        title: "1. Promotional messages",
        paragraphs: [
          "We send promotional email, text, or similar direct marketing only when we have the permission or other lawful basis required for the channel and location. Signing up is optional and is not a condition of completing a standard purchase unless clearly stated for a separate promotional benefit.",
        ],
      },
      {
        title: "2. What messages may contain",
        paragraphs: [
          "Marketing may include product news, restock notices, offers, surveys, events, or brand updates. Message frequency may vary. Standard carrier or data charges may apply to text messages.",
        ],
      },
      {
        title: "3. Unsubscribing",
        paragraphs: [
          "Use the unsubscribe link in an email, follow the stop instruction in a text, adjust an available preference setting, or contact cost@averlynxstore.one. We will process the request within the period required by law. A short delay may occur while systems update.",
        ],
      },
      {
        title: "4. Service communications",
        paragraphs: [
          "Opting out of marketing does not stop non-promotional messages needed to complete a transaction, provide requested support, deliver a security or safety notice, or communicate a material policy change.",
        ],
      },
      {
        title: "5. Third-party promotions",
        paragraphs: [
          "We do not authorize unrelated parties to send messages that falsely appear to come from averlynxstore. Report suspicious communications and do not provide payment credentials in response to an unexpected message.",
        ],
      },
    ],
  },
  {
    slug: "regional-privacy",
    title: "Regional Privacy Rights & Requests",
    summary: "A practical route for access, correction, deletion, opt-out, and related privacy requests.",
    group: "Legal & Privacy",
    sections: [
      {
        title: "1. Rights may vary",
        paragraphs: [
          "Privacy rights depend on your location and on whether a particular law applies to averlynxstore or the processing. Rights may include access, correction, deletion, restriction, portability, objection, withdrawal of consent, opting out of direct marketing, and appealing a denied request.",
        ],
      },
      {
        title: "2. Hong Kong",
        paragraphs: [
          "Where the Personal Data (Privacy) Ordinance applies, individuals may request access to and correction of their personal data and may opt out of direct marketing. Direct-marketing choices will be honored in accordance with applicable requirements.",
        ],
      },
      {
        title: "3. EEA, United Kingdom, and similar jurisdictions",
        paragraphs: [
          "Where applicable, you may have rights to be informed, access, correction, erasure, restriction, portability, objection, withdrawal of consent, and review of certain solely automated decisions. You may also complain to the data-protection authority for your location.",
        ],
      },
      {
        title: "4. United States state privacy laws",
        paragraphs: [
          "Where an applicable state law provides them, you may have rights to know or access, correct, delete, obtain a portable copy, opt out of sale, targeted advertising, or certain profiling, limit specified uses of sensitive data, and receive equal service when exercising a right. We do not exchange personal information for money. Some laws may define advertising-related disclosures more broadly than an ordinary sale.",
        ],
      },
      {
        title: "5. Submitting and verifying a request",
        paragraphs: [
          "Email cost@averlynxstore.one with the subject 'Privacy Request' and describe the right you want to exercise. We may verify information already associated with your order or account. Do not send a passport, national identifier, or other sensitive document unless we specifically explain why it is necessary and provide a secure method.",
          "An authorized agent may submit a request where permitted, but we may ask for proof of authority and direct confirmation from you. We will respond within the period required by applicable law and explain any lawful limitation or appeal route.",
        ],
      },
    ],
  },
  {
    slug: "fraud-security",
    title: "Fraud Prevention & Account Security Policy",
    summary: "Order screening, verification, account protection, and suspicious-activity reporting.",
    group: "Safety & Support",
    sections: [
      {
        title: "1. Protective checks",
        paragraphs: [
          "We and our payment, fraud-prevention, hosting, and delivery providers may use order and device information to identify unusual activity, verify transactions, enforce purchase limits, and protect customers and the service.",
        ],
      },
      {
        title: "2. Additional verification",
        paragraphs: [
          "We may pause an order and request limited information reasonably needed to verify the purchaser, payment, or delivery address. We will not ask you to send a full payment-card number or account password by ordinary email.",
        ],
      },
      {
        title: "3. Customer responsibilities",
        paragraphs: [
          "Use a unique password where accounts are available, keep order and gift-card codes private, review confirmation messages, and contact us promptly about an account change or order you do not recognize.",
        ],
      },
      {
        title: "4. Holds and cancellations",
        paragraphs: [
          "We may hold, limit, or cancel activity when there is a reasonable security, fraud, sanctions, resale, or payment concern. We will refund collected amounts for cancelled items unless funds must be handled differently under law or a payment-provider process.",
        ],
      },
      {
        title: "5. Reporting",
        paragraphs: [
          "Forward suspicious messages or report account and payment concerns to cost@averlynxstore.one. Contact your bank immediately if payment credentials may be compromised.",
        ],
      },
    ],
  },
  {
    slug: "customer-care",
    title: "Customer Care Policy",
    summary: "Support channels, information to provide, identity checks, records, and respectful communication.",
    group: "Safety & Support",
    sections: [
      {
        title: "1. Contact channel",
        paragraphs: [
          "Email cost@averlynxstore.one for order, product, delivery, return, accessibility, privacy, or policy assistance. Include the order number when relevant, but never send a password or full payment-card number.",
        ],
      },
      {
        title: "2. Response and investigation",
        paragraphs: [
          "We aim to acknowledge requests as soon as reasonably possible. Complex matters, carrier investigations, payment reviews, product inspections, and privacy requests may require additional time. Any deadline required by law takes priority.",
        ],
      },
      {
        title: "3. Verification",
        paragraphs: [
          "Before discussing an order or personal data, we may ask for proportionate information that matches our records. This helps prevent disclosure to the wrong person.",
        ],
      },
      {
        title: "4. Respectful communication",
        paragraphs: [
          "We welcome criticism and complaints. Threats, harassment, discrimination, repeated spam, or abusive language toward staff or other customers may lead us to limit a channel while preserving access to legally required support.",
        ],
      },
      {
        title: "5. Support records",
        paragraphs: [
          "We may retain support correspondence as described in the Privacy Policy to resolve the request, train or supervise service quality, prevent fraud, and meet legal obligations.",
        ],
      },
    ],
  },
  {
    slug: "complaints-disputes",
    title: "Complaints & Dispute Resolution Policy",
    summary: "How to raise, escalate, and seek a fair resolution to a concern.",
    group: "Safety & Support",
    sections: [
      {
        title: "1. Raising a complaint",
        paragraphs: [
          "Email cost@averlynxstore.one with the subject 'Complaint'. Include your contact details, order number if relevant, a clear description, key dates, supporting material, and the resolution you are seeking.",
        ],
      },
      {
        title: "2. Our review",
        paragraphs: [
          "We will review the information, check relevant records, and may ask focused follow-up questions. We aim to provide a reasoned response and, where appropriate, a practical remedy such as correction, replacement, refund, or another lawful resolution.",
        ],
      },
      {
        title: "3. Escalation",
        paragraphs: [
          "If you disagree with the first response, reply and ask for escalation, explaining what you believe was missed. When possible, a person not responsible for the original decision will review the matter.",
        ],
      },
      {
        title: "4. External options",
        paragraphs: [
          "Nothing in this policy prevents you from contacting a payment provider, consumer-protection body, privacy regulator, court, or an available alternative dispute-resolution service. We do not require informal review before you use a remedy that applicable law allows you to pursue immediately.",
        ],
      },
    ],
  },
  {
    slug: "responsible-sourcing",
    title: "Responsible Sourcing Statement",
    summary: "Supplier expectations for lawful, safe, and respectful production practices.",
    group: "Company Standards",
    sections: [
      {
        title: "1. Scope",
        paragraphs: [
          "This statement describes the standards averlynxstore expects suppliers and business partners involved in producing or fulfilling our products to support. It is a statement of direction and expectations, not a certification that every risk has been eliminated.",
        ],
      },
      {
        title: "2. Workplace expectations",
        bullets: [
          "Freely chosen employment and no forced, trafficked, or unlawful child labor",
          "Lawful pay, working time, and employment records",
          "A safe and healthy workplace",
          "No unlawful discrimination, harassment, or retaliation",
          "Respect for lawful freedom of association and worker voice",
          "Compliance with applicable environmental and product-safety requirements",
        ],
      },
      {
        title: "3. Risk and improvement",
        paragraphs: [
          "We may communicate standards, request information, review higher-risk issues, and seek corrective action when concerns are identified. The appropriate response depends on severity, credibility, leverage, and whether improvement can be achieved safely.",
        ],
      },
      {
        title: "4. Reporting a concern",
        paragraphs: [
          "Workers, suppliers, customers, and other stakeholders may report a sourcing or workplace concern to cost@averlynxstore.one. Provide facts and supporting information where safe. Retaliation for a good-faith report is not acceptable.",
        ],
      },
    ],
  },
  {
    slug: "authenticity-resale",
    title: "Authenticity, Bulk Orders & Resale Policy",
    summary: "Protection against counterfeit activity, unauthorized branding, and abusive bulk purchasing.",
    group: "Company Standards",
    sections: [
      {
        title: "1. Authenticity",
        paragraphs: [
          "Products purchased directly from this website are supplied as averlynxstore products. We cannot verify the authenticity, condition, storage, or chain of custody of goods offered by an unrelated third party.",
        ],
      },
      {
        title: "2. No implied authorization",
        paragraphs: [
          "Buying a product does not grant permission to use averlynxstore names, logos, product images, packaging, or other protected material in a way that implies an official reseller, distributor, sponsorship, or partnership relationship.",
        ],
      },
      {
        title: "3. Bulk and automated orders",
        paragraphs: [
          "We may limit quantities or review orders that appear intended for unauthorized commercial resale, evade purchase limits, use automation unfairly, or create supply or fraud risks. Legitimate group or business-purchase inquiries may be sent to cost@averlynxstore.one before ordering.",
        ],
      },
      {
        title: "4. Counterfeit and impersonation reports",
        paragraphs: [
          "Report suspected counterfeit listings, misleading domains, impersonation accounts, or unauthorized use of averlynxstore branding to cost@averlynxstore.one. Include the link, seller information, screenshots, and purchase details if available. Do not put yourself at risk to gather evidence.",
        ],
      },
    ],
  },
];

export const policyGroups = [
  "Shopping",
  "Products",
  "Legal & Privacy",
  "Safety & Support",
  "Company Standards",
];

export const findPolicy = (slug) =>
  policyData.find((policy) => policy.slug === slug);
